/**
 * PostcardTeaser — Sprint 4B
 * A sober accordion for the TRIP city's postcard, shown under the constellation.
 * Collapsed = one quiet phrase (keeps Home editorial). Expanded = the postcard
 * preview + how to earn it (complete the constellation) or buy it now. If it's
 * already earned, it offers the printed-postcard link instead. Read-only.
 *
 * The postcard is always the trip's city — never a chooser. Earn needs gems; buy
 * is always available (even with no gems authored).
 */
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  cityGemStatus, getUnlockedPostcards, buildBuyUrl, buildRedeemUrl,
} from '../utils/postcardManager';
import PostcardCard from './PostcardCard.jsx';
import theme from '../styles/theme';

export default function PostcardTeaser({ country, city, defaultOpen = false }) {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(defaultOpen);
  const [status, setStatus] = useState(null);
  const [card, setCard] = useState(null);     // the unlocked postcard, if any
  const [buyUrl, setBuyUrl] = useState(null);

  const refresh = useCallback(() => {
    let cancelled = false;
    if (!country || !city) { setStatus(null); return () => {}; }
    (async () => {
      const [s, cards, bu] = await Promise.all([
        cityGemStatus(country, city).catch(() => null),
        getUnlockedPostcards().catch(() => []),
        buildBuyUrl(country, city, i18n.language).catch(() => null),
      ]);
      if (cancelled) return;
      setStatus(s);
      setBuyUrl(bu);
      const cid = s?.cityId;
      setCard(cards.find(c => c.cityId === cid) || null);
    })();
    return () => { cancelled = true; };
  }, [country, city, i18n.language]);

  useEffect(() => refresh(), [refresh]);
  useEffect(() => {
    const on = () => refresh();
    window.addEventListener('wayfare-postcard-unlocked', on);
    window.addEventListener('wayfare-gem-surfaced', on);
    return () => {
      window.removeEventListener('wayfare-postcard-unlocked', on);
      window.removeEventListener('wayfare-gem-surfaced', on);
    };
  }, [refresh]);

  if (!country || !city) return null;

  const earned = !!card;
  const canEarn = (status?.total || 0) > 0 && !earned;
  const redeemUrl = earned ? buildRedeemUrl(card, i18n.language) : null;

  return (
    <div style={S.wrap}>
      <button type="button" style={S.header} onClick={() => setOpen(o => !o)}
        aria-expanded={open}>
        <span style={S.headerText}>{t('postcard.teaserTitle', { city })}</span>
        <span style={{ ...S.chev, transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
      </button>

      {open && (
        <div style={S.body}>
          <PostcardCard city={city} country={country} img={card?.img} />

          {earned ? (
            <>
              <p style={S.line}>{t('postcard.teaserEarned')}</p>
              {redeemUrl && (
                <a href={redeemUrl} target="_blank" rel="noopener noreferrer" style={S.primary}>
                  {t('postcard.physicalLink')}
                </a>
              )}
            </>
          ) : (
            <>
              <p style={S.line}>
                {canEarn ? t('postcard.teaserEarn') : t('postcard.teaserBuyOnly')}
              </p>
              {buyUrl && (
                <a href={buyUrl} target="_blank" rel="noopener noreferrer" style={S.primary}>
                  {t('postcard.buy')}
                </a>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const S = {
  wrap: { background: theme.cream, border: `1px solid ${theme.creamDeep}`, borderRadius: 14, overflow: 'hidden' },
  header: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 10, padding: '13px 16px', background: 'transparent', border: 'none', cursor: 'pointer',
    textAlign: 'left',
  },
  headerText: { fontSize: 14, fontWeight: 600, color: theme.brownDark, fontStyle: 'italic' },
  chev: { color: theme.blue, fontSize: 13, transition: 'transform 200ms ease' },
  body: { padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 },
  line: { margin: 0, fontSize: 13.5, lineHeight: 1.5, color: theme.brown, textAlign: 'center' },
  primary: {
    display: 'block', textAlign: 'center', textDecoration: 'none',
    padding: '11px 0', background: theme.terracotta, color: '#fff',
    borderRadius: 10, fontSize: 14, fontWeight: 700,
  },
};
